/**
 * Which catalog products capture a PANEL COUNT at quotation (Ben, 2026-09-15).
 *
 * "Number of panels is largely determined during quotation" — for these
 * families the rep and the customer settle the panel split while agreeing the
 * job, so the number belongs on the quote line rather than being discovered
 * weeks later on site. The measurer still confirms it: `panelCount` remains a
 * fabrication field, seeded from the line at opening expansion and editable.
 *
 * DESCRIPTIVE ONLY — every product here is `Area` priced, so the panel count
 * never enters any pricing calculation. Do not make it a pricing input; a
 * per-panel price is expressed by a `Panel` pricing_method, where `qty` is the
 * count (`AWN-006..011`, `ASD-004/005`). Those add-ons deliberately do NOT
 * auto-follow the base line's panel count — syncing them would move money.
 *
 * Deliberately excluded:
 *   · ASW-001/002 — named "Swing Door - 1 Panel"; the count is in the product.
 *   · AWN-003 Fixed Panel — a single fixed panel.
 *   · TWN bases — "1col/2col/3col" is the product choice, and they are Set
 *     priced, so the count IS which product you picked.
 */
export const PANEL_COUNT_CODES: ReadonlySet<string> = new Set([
  // Slimness — interlocking + folding (the suspended-track set)
  'SSL-001', // Interlocking Door (wall to wall)
  'SSL-002', // Interlocking Door (track extensions)
  'SSL-003', // Folding Door - Centre Fold
  'SSL-004', // Folding Door - Side Fold
  // Aluminium doors
  'ASD-001', // Sliding Door - Top and Bottom track
  // Aluminium windows
  'AWN-001', // Flexi Window
  'AWN-002', // Casement Window
  'AWN-004', // Sliding Window (2way track)
  'AWN-005', // Sliding Window (3way track)
  // Window grilles — added 2026-09-15; they already capture
  // openablePanelCount at measurement, so the quoted total lines them up
  'WGL-001', // Window Grille - 2way Sliding
  'WGL-002', // Window Grille - 3way Sliding
  'WGL-003', // Window Grille - Swing
]);

export const capturesPanelCount = (prdCode: string | null | undefined): boolean =>
  !!prdCode && PANEL_COUNT_CODES.has(prdCode);

/**
 * Parses the line's stored panel count for display and for seeding the
 * measurer's field. Blank is a valid answer (the field is optional) and so is
 * an absent key on any quote saved before 2026-09-15.
 *
 * The stored value is a STRING — `widthMm` / `heightMm` are strings on the
 * same interface, and blank must be `''` rather than `undefined`: the cart is
 * copied verbatim into an order revision by a Cloud Function on the Admin SDK,
 * which has no `ignoreUndefinedProperties` and throws on an undefined at
 * `ref.set()`. A number is accepted too, so a hand-edited document still reads.
 */
export const parsePanelCount = (v: string | number | null | undefined): number | null => {
  if (v === null || v === undefined) return null;
  const n = typeof v === 'number' ? v : Number(String(v).trim());
  if (String(v).trim() === '' || !Number.isFinite(n) || n <= 0) return null;
  return Math.round(n);
};
